import React, { useState, useEffect } from "react"
import { Card, Checkbox, Typography, Flex, Spin, Empty } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import PropTypes from "prop-types"
import { getCustomFees } from "store/custom-fees"
import { getGuideTrip, updateTrip } from "store/trips"
import { loadingSelector } from "modules/loader-watchers/selectors"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import styles from "./index.module.css"

const { Title, Text } = Typography

const FeeItem = ({ fee, checked, onChange }) => (
  <div className={styles.feeItem}>
    <Checkbox checked={checked} onChange={(e) => onChange(fee.id, e.target.checked)}>
      <Flex vertical gap={2}>
        <Text className={styles.feeName}>{fee.title}</Text>
        <Text className={styles.feePrice}>
          {fee.fee_type === "percentage" ? `${Number(fee.price).toFixed(2)}%` : `$${Number(fee.price).toFixed(2)}`}
        </Text>
      </Flex>
    </Checkbox>
  </div>
)

FeeItem.propTypes = {
  fee: PropTypes.shape({
    id: PropTypes.number.isRequired,
    title: PropTypes.string.isRequired,
    price: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    fee_type: PropTypes.string.isRequired
  }).isRequired,
  checked: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired
}

const ListingTaxFees = () => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const customFees = useSelector((state) => state.customFees?.items || [])
  const feesLoading = useSelector((state) => state.customFees?.loading || false)
  const tripLoading = useSelector(loadingSelector("trips.trip"))

  const activeFees = customFees.filter((fee) => fee.is_active)

  const [selectedFeeIds, setSelectedFeeIds] = useState([])

  useEffect(() => {
    dispatch(getCustomFees())
    dispatch(getGuideTrip(id)).then((res) => {
      const ids =
        res?.data?.assigned_custom_fee_ids ||
        res?.data?.assigned_custom_fees?.map((f) => f.id) ||
        []
      setSelectedFeeIds(ids)
    })
  }, [dispatch, id])

  const handleChange = (feeId, checked) => {
    setSelectedFeeIds((prev) => (checked ? [...prev, feeId] : prev.filter((id) => id !== feeId)))
  }

  const onFinish = (callback) => {
    const formData = new FormData()
    selectedFeeIds.forEach((feeId) => formData.append("trip[assigned_custom_fee_ids][]", feeId))
    formData.append("trip[wizard_step]", "tax_fees")
    dispatch(updateTrip(id, formData))
    if (callback) callback()
  }

  return (
    <Spin spinning={feesLoading || tripLoading}>
      <Card className={styles.card}>
        <Flex justify="space-between" align="center" className={styles.header}>
          <Title level={3} className={styles.title}>
            {t("listings.tabs.tax_fees.card_title")}
          </Title>
        </Flex>
        <Text className={styles.description}>{t("listings.tabs.tax_fees.description")}</Text>

        {!feesLoading && activeFees.length === 0 ? (
          <Empty description={t("listings.tabs.tax_fees.no_fees")} className={styles.empty} />
        ) : (
          <div className={styles.feeList}>
            {activeFees.map((fee) => (
              <FeeItem key={fee.id} fee={fee} checked={selectedFeeIds.includes(fee.id)} onChange={handleChange} />
            ))}
          </div>
        )}
      </Card>
      <ListingActionButtons onFinish={onFinish} />
    </Spin>
  )
}

export default ListingTaxFees
